import type { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import type { AutoPromptEffort, AutoPromptReport, OnboardingJob, OnboardingJobInputKind, OnboardingJobItem } from "../../../services/backend.service";
import { backendService } from "../../../services/backend.service";
import { type FailedTab, useLoadNewNurses, useLoadScheduleRecurringShifts, useLoadScheduleShifts, useUpdateDepartmentFirstSunday } from "../hooks";
import { earliestDateInCsv, type LoadOrderEntry, orderFilesChronologically, SHIFTS_DATE_COLUMN } from "../loadOrder";
import {
	isOnboardingArtifactUnavailableError,
	isOnboardingJobActive,
	useCreateAutoPromptJob,
	useCreateCsvTransformJob,
	useOnboardingJob,
	useRetryOnboardingJobItem,
} from "../useOnboardingJobs";
import { stripExtension } from "../utils";
import type { LoadRecurringResult, LoadShiftsResult } from "./LoadResultDisplay";
import type { LoadNursesResults } from "./NursesSummary";
import type { PreviewFileType } from "./PreviewCard";

export type AutoPromptFileKind = "roster" | "shifts" | "template";

export type AutoPromptFile = {
	id: string;
	file: File;
	kind: AutoPromptFileKind;
};

export type TransformOutcome = {
	csvBuffer: ArrayBuffer;
	earliestDate?: DateTime | null;
	filteredBlob?: Blob;
	failedTabs: FailedTab[];
	skip: boolean;
	uploaded: boolean;
	promptHistoryId?: string;
	loadResult?: LoadShiftsResult | LoadRecurringResult | LoadNursesResults | null;
};

export type TransformFailure = {
	itemId: string;
	errorKey: string;
	skip: boolean;
};

export type Phase = "upload" | "analysis" | "prompt" | "transform";
export type TransformOutcomes = Record<string, TransformOutcome>;

export type AutoPromptEditable = {
	schedule_symbols: string;
	locations: string;
	master_schedule_orientation: string;
	shift_times: string;
	name_matching: string;
	file_dates: string;
};

export const KIND_LABEL_KEY: Record<AutoPromptFileKind, string> = {
	roster: "onboarding_load.kind.roster",
	shifts: "onboarding_load.kind.shifts",
	template: "onboarding_load.kind.template",
};

export const EFFORT_LABEL_KEY: Record<AutoPromptEffort, string> = {
	low: "onboarding_auto_prompt.effort.low",
	medium: "onboarding_auto_prompt.effort.medium",
	high: "onboarding_auto_prompt.effort.high",
	xhigh: "onboarding_auto_prompt.effort.xhigh",
	max: "onboarding_auto_prompt.effort.max",
};

export const EFFORT_ORDER: readonly AutoPromptEffort[] = ["low", "medium", "high", "xhigh", "max"];
export const DEFAULT_EFFORT: AutoPromptEffort = "high";
export const KIND_ORDER: readonly AutoPromptFileKind[] = ["roster", "shifts", "template"];
export const previewFileType = (kind: AutoPromptFileKind): PreviewFileType => (kind === "roster" ? "nurses" : kind);

export const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const SECTION_HEADER_KEY: Record<keyof AutoPromptEditable, string> = {
	schedule_symbols: "onboarding_auto_prompt.section.schedule_symbols",
	locations: "onboarding_auto_prompt.section.locations",
	master_schedule_orientation: "onboarding_auto_prompt.section.master_schedule_orientation",
	shift_times: "onboarding_auto_prompt.section.shift_times",
	name_matching: "onboarding_auto_prompt.section.name_matching",
	file_dates: "onboarding_auto_prompt.section.file_dates",
};

export const SECTION_ORDER: (keyof AutoPromptEditable)[] = [
	"schedule_symbols",
	"locations",
	"master_schedule_orientation",
	"shift_times",
	"name_matching",
	"file_dates",
];

const errorMessage = (error: unknown): string | undefined => (error as Error)?.message;
const kindFromInput = (kind: OnboardingJobInputKind): AutoPromptFileKind => (kind === "nurses" ? "roster" : kind);
const sourceFile = (item: OnboardingJobItem, bytes: ArrayBuffer): AutoPromptFile => ({
	id: item.id,
	kind: kindFromInput(item.input_kind),
	file: new File([bytes], item.display_filename),
});

export function buildEditableFromReport(report: AutoPromptReport): AutoPromptEditable {
	return {
		schedule_symbols: report.schedule_symbols,
		locations: report.locations,
		master_schedule_orientation: report.master_schedule_orientation === null ? "" : report.master_schedule_orientation,
		shift_times: report.shift_times,
		name_matching: report.name_matching,
		file_dates: report.file_dates,
	};
}

export function assembleFinalPrompt(
	editable: AutoPromptEditable,
	flags: string[],
	sectionHeaders: Record<keyof AutoPromptEditable, string>,
	flagsHeader: string,
): string {
	const blocks: string[] = [];
	for (const key of SECTION_ORDER) {
		const value = editable[key].trim();
		if (!value) continue;
		blocks.push(`## ${sectionHeaders[key]}\n${value}`);
	}
	if (flags.length > 0) blocks.push(`## ${flagsHeader}:\n${flags.map((flag) => `- ${flag}`).join("\n")}`);
	return blocks.join("\n\n");
}

type Params = {
	restoredJob?: OnboardingJob;
	onJobCreated?: (job: OnboardingJob) => void;
};

export function useAutoPromptSection({ restoredJob, onJobCreated }: Params) {
	const { t } = useTranslation();
	const { chosenDepartment, setChosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { showSnackbar } = useSnackbar();
	const { copyToClipboard } = useCopyToClipboard();
	const { track, trackError } = useAnalytics();
	const departmentId = chosenDepartment?.department_id;

	const [phase, setPhase] = useState<Phase>("upload");
	const [files, setFiles] = useState<AutoPromptFile[]>([]);
	const filesRef = useRef(files);
	filesRef.current = files;
	const [effort, setEffort] = useState<AutoPromptEffort>(DEFAULT_EFFORT);
	const [editable, setEditable] = useState<AutoPromptEditable | null>(null);
	const [flags, setFlags] = useState<string[]>([]);
	const [finalPrompt, setFinalPrompt] = useState("");
	const [transformed, setTransformed] = useState<TransformOutcomes>({});
	const [transformFailures, setTransformFailures] = useState<Record<string, TransformFailure>>({});
	const [showWeekParityDialog, setShowWeekParityDialog] = useState(false);
	const [artifactsUnavailable, setArtifactsUnavailable] = useState(false);
	const [analysisJobId, setAnalysisJobId] = useState<string>();
	const [transformJobId, setTransformJobId] = useState<string>();
	const analysisOutputHydrated = useRef<string | undefined>(undefined);
	const restoredSources = useRef(new Set<string>());
	const transformOutputIds = useRef(new Set<string>());
	const fileIdByTransformItemId = useRef(new Map<string, string>());

	const createAnalysisJob = useCreateAutoPromptJob();
	const createTransformJob = useCreateCsvTransformJob();
	const retryTransformItem = useRetryOnboardingJobItem();
	const analysisQuery = useOnboardingJob(analysisJobId, departmentId);
	const transformQuery = useOnboardingJob(transformJobId, departmentId);
	const restoredJobForDepartment = restoredJob?.department_id === departmentId ? restoredJob : undefined;
	const analysisJob = analysisQuery.data ? analysisQuery.data : restoredJobForDepartment?.id === analysisJobId ? restoredJobForDepartment : undefined;
	const transformJob = transformQuery.data ? transformQuery.data : restoredJobForDepartment?.id === transformJobId ? restoredJobForDepartment : undefined;

	const { mutateAsync: loadNewNurses, isPending: isLoadingNurses } = useLoadNewNurses();
	const { mutateAsync: loadScheduleShifts, isPending: isLoadingShifts } = useLoadScheduleShifts();
	const { mutateAsync: loadRecurringShifts, isPending: isLoadingTemplate } = useLoadScheduleRecurringShifts();
	const { mutateAsync: performUpdateFirstSunday, isPending: isUpdatingFirstSunday } = useUpdateDepartmentFirstSunday();

	const hasAtLeastOneFile = files.length > 0;
	const hasRanAnalysis = editable !== null;
	const filesByKind: Record<AutoPromptFileKind, AutoPromptFile[]> = files.reduce<Record<AutoPromptFileKind, AutoPromptFile[]>>(
		(accumulator, file) => {
			accumulator[file.kind].push(file);
			return accumulator;
		},
		{ roster: [], shifts: [], template: [] },
	);

	const dateOfFile = (file: AutoPromptFile): DateTime | null => {
		if (file.kind !== "shifts") return null;
		const transformedDate = transformed[file.id]?.earliestDate;
		return transformedDate === undefined ? null : transformedDate;
	};
	const orderedFilesOfKind = (kind: AutoPromptFileKind): AutoPromptFile[] => orderFilesChronologically(filesByKind[kind], dateOfFile);
	const orderedFiles: AutoPromptFile[] = KIND_ORDER.flatMap((kind) => orderedFilesOfKind(kind));
	const loadOrderEntries: LoadOrderEntry[] = orderedFiles.map((file) => ({
		id: file.id,
		label: `${t(KIND_LABEL_KEY[file.kind])}: ${file.file.name}`,
		date: dateOfFile(file),
	}));

	const restoreSourcesFor = useCallback(
		async (job: OnboardingJob): Promise<void> => {
			if (restoredSources.current.has(job.id)) return;
			restoredSources.current.add(job.id);
			try {
				const restored = await Promise.all(
					job.items.map(async (item) => {
						const response = await backendService.getOnboardingJobItemSource(job.id, item.id, job.department_id);
						return sourceFile(item, response.data);
					}),
				);
				setFiles(restored);
			} catch (error) {
				if (isOnboardingArtifactUnavailableError(error)) setArtifactsUnavailable(true);
				restoredSources.current.delete(job.id);
				trackError("auto_prompt_restore_sources_error", { department_id: job.department_id, job_id: job.id, error_message: errorMessage(error) });
				showErrorSnackbar(t("onboarding_jobs.errors.restore"), { department_id: job.department_id, job_id: job.id }, error);
			}
		},
		[showErrorSnackbar, t, trackError],
	);

	useEffect(() => {
		if (restoredJobForDepartment?.origin !== "auto_prompt") return;
		if (restoredJobForDepartment.job_type === "auto_prompt_analysis") {
			setAnalysisJobId(restoredJobForDepartment.id);
			if (filesRef.current.length === 0) void restoreSourcesFor(restoredJobForDepartment);
			return;
		}
		setTransformJobId(restoredJobForDepartment.id);
		if (restoredJobForDepartment.source_job_id) setAnalysisJobId(restoredJobForDepartment.source_job_id);
		setPhase("transform");
		fileIdByTransformItemId.current = new Map(
			restoredJobForDepartment.items.flatMap((item) => {
				const existingFile = filesRef.current[item.ordinal];
				return [[item.id, existingFile ? existingFile.id : item.id]];
			}),
		);
		if (filesRef.current.length === 0) {
			void restoreSourcesFor(restoredJobForDepartment);
		}
	}, [restoredJobForDepartment, restoreSourcesFor]);

	useEffect(() => {
		if (analysisJob?.status !== "succeeded" || analysisOutputHydrated.current === analysisJob.id) return;
		analysisOutputHydrated.current = analysisJob.id;
		void backendService
			.getAutoPromptJobOutput(analysisJob.id, analysisJob.department_id)
			.then((response) => {
				const nextEditable = buildEditableFromReport(response.data);
				setEditable(nextEditable);
				setFlags(response.data.flags);
				setFinalPrompt(
					assembleFinalPrompt(
						nextEditable,
						response.data.flags,
						{
							schedule_symbols: t(SECTION_HEADER_KEY.schedule_symbols),
							locations: t(SECTION_HEADER_KEY.locations),
							master_schedule_orientation: t(SECTION_HEADER_KEY.master_schedule_orientation),
							shift_times: t(SECTION_HEADER_KEY.shift_times),
							name_matching: t(SECTION_HEADER_KEY.name_matching),
							file_dates: t(SECTION_HEADER_KEY.file_dates),
						},
						t("onboarding_auto_prompt.section.flags"),
					),
				);
				if (!transformJobId) setPhase("analysis");
				track("auto_prompt_analyze_success", { department_id: analysisJob.department_id, job_id: analysisJob.id, flag_count: response.data.flags.length });
			})
			.catch((error) => {
				if (isOnboardingArtifactUnavailableError(error)) setArtifactsUnavailable(true);
				analysisOutputHydrated.current = undefined;
				trackError("auto_prompt_analysis_download_error", {
					department_id: analysisJob.department_id,
					job_id: analysisJob.id,
					error_message: errorMessage(error),
				});
				showErrorSnackbar(t("onboarding_jobs.errors.download_result"), { department_id: analysisJob.department_id, job_id: analysisJob.id }, error);
			});
	}, [analysisJob, showErrorSnackbar, t, track, trackError, transformJobId]);

	useEffect(() => {
		if (!transformJob) return;
		for (const item of transformJob.items) {
			const fileId = fileIdByTransformItemId.current.get(item.id);
			if (!fileId) continue;
			if (item.status === "failed" || item.status === "cancelled") {
				setTransformFailures((previous) => {
					const errorKey = item.user_error_key ? item.user_error_key : `onboarding_jobs.item.${item.status}_description`;
					const existing = previous[fileId];
					if (existing?.itemId === item.id && existing.errorKey === errorKey) return previous;
					return { ...previous, [fileId]: { itemId: item.id, errorKey, skip: existing?.skip === true } };
				});
				continue;
			}
			if (item.status !== "succeeded" || transformOutputIds.current.has(item.id)) continue;
			transformOutputIds.current.add(item.id);
			void backendService
				.getOnboardingJobItemOutput(transformJob.id, item.id, transformJob.department_id)
				.then((response) => {
					const kind = kindFromInput(item.input_kind);
					setTransformFailures((previous) => {
						const next = { ...previous };
						delete next[fileId];
						return next;
					});
					setTransformed((previous) => ({
						...previous,
						[fileId]: {
							csvBuffer: response.data,
							failedTabs: item.failed_tabs,
							skip: false,
							uploaded: false,
							promptHistoryId: item.prompt_history_id === null ? undefined : item.prompt_history_id,
							earliestDate: kind === "shifts" ? earliestDateInCsv(response.data, SHIFTS_DATE_COLUMN) : null,
						},
					}));
				})
				.catch((error) => {
					if (isOnboardingArtifactUnavailableError(error)) setArtifactsUnavailable(true);
					transformOutputIds.current.delete(item.id);
					trackError("auto_prompt_transform_download_error", {
						department_id: transformJob.department_id,
						job_id: transformJob.id,
						item_id: item.id,
						error_message: errorMessage(error),
					});
					showErrorSnackbar(t("onboarding_jobs.errors.download_result"), { department_id: transformJob.department_id, job_id: transformJob.id }, error);
				});
		}
	}, [showErrorSnackbar, t, trackError, transformJob]);

	const addFiles = (incoming: File[]) => {
		const added: AutoPromptFile[] = incoming.map((file) => ({ id: crypto.randomUUID(), file, kind: "roster" }));
		setFiles((previous) => [...previous, ...added]);
	};

	const handleKindChange = (id: string, kind: AutoPromptFileKind) => {
		setFiles((previous) => previous.map((file) => (file.id === id ? { ...file, kind } : file)));
		setTransformed((previous) => {
			if (!previous[id]) return previous;
			const next = { ...previous };
			delete next[id];
			return next;
		});
	};

	const handleDeleteFile = (id: string) => setFiles((previous) => previous.filter((file) => file.id !== id));

	const resetWizard = () => {
		setFiles([]);
		setEditable(null);
		setFlags([]);
		setFinalPrompt("");
		setTransformed({});
		setTransformFailures({});
		setAnalysisJobId(undefined);
		setTransformJobId(undefined);
		setShowWeekParityDialog(false);
		setArtifactsUnavailable(false);
		setPhase("upload");
		analysisOutputHydrated.current = undefined;
		restoredSources.current.clear();
		transformOutputIds.current.clear();
		fileIdByTransformItemId.current.clear();
	};

	const handleAnalyze = async () => {
		if (!departmentId || !hasAtLeastOneFile || artifactsUnavailable) return;
		const properties = {
			department_id: departmentId,
			file_count: files.length,
			roster_count: filesByKind.roster.length,
			shifts_count: filesByKind.shifts.length,
			template_count: filesByKind.template.length,
			effort,
		};
		track("auto_prompt_analyze_start", properties);
		try {
			const canonicalInput = KIND_ORDER.flatMap((kind) => filesByKind[kind]);
			const job = await createAnalysisJob.mutateAsync({
				departmentId,
				effort,
				idempotencyKey: crypto.randomUUID(),
				rosterFiles: filesByKind.roster.map((file) => file.file),
				shiftsFiles: filesByKind.shifts.map((file) => file.file),
				templateFiles: filesByKind.template.map((file) => file.file),
			});
			if (job.items.length !== canonicalInput.length) throw new Error("Auto Prompt source item count did not match the submitted files");
			const canonicalFiles = job.items.map((item): AutoPromptFile => {
				const submittedFile = canonicalInput[item.ordinal];
				if (!submittedFile) throw new Error("Auto Prompt source item did not match a submitted file");
				return { ...submittedFile, id: item.id };
			});
			setFiles(canonicalFiles);
			setAnalysisJobId(job.id);
			setPhase("analysis");
			analysisOutputHydrated.current = undefined;
			onJobCreated?.(job);
		} catch (error) {
			trackError("auto_prompt_analyze_error", { ...properties, error_message: errorMessage(error) });
		}
	};

	const handleGeneratePrompt = () => {
		if (!editable) return;
		setFinalPrompt(
			assembleFinalPrompt(
				editable,
				flags,
				{
					schedule_symbols: t(SECTION_HEADER_KEY.schedule_symbols),
					locations: t(SECTION_HEADER_KEY.locations),
					master_schedule_orientation: t(SECTION_HEADER_KEY.master_schedule_orientation),
					shift_times: t(SECTION_HEADER_KEY.shift_times),
					name_matching: t(SECTION_HEADER_KEY.name_matching),
					file_dates: t(SECTION_HEADER_KEY.file_dates),
				},
				t("onboarding_auto_prompt.section.flags"),
			),
		);
		track("auto_prompt_generate_prompt", { department_id: departmentId });
		setPhase("prompt");
	};

	const handleCopyPrompt = async () => {
		if (!finalPrompt) return;
		const copied = await copyToClipboard(finalPrompt);
		showSnackbar(copied ? t("onboarding_auto_prompt.copied") : t("onboarding_auto_prompt.copy_failed"), copied ? "success" : "error");
	};

	const handleBackToEditing = () => setPhase("analysis");

	const handleTransformAll = async () => {
		if (artifactsUnavailable || !departmentId || !finalPrompt.trim()) return;
		if (!analysisJobId || !analysisJob || analysisJob.department_id !== departmentId) return;
		track("auto_prompt_transform_start", { department_id: departmentId, source_job_id: analysisJobId, file_count: analysisJob.items.length });
		try {
			const job = await createTransformJob.mutateAsync({
				departmentId,
				idempotencyKey: crypto.randomUUID(),
				origin: "auto_prompt",
				sourceJobId: analysisJobId,
				items: analysisJob.items.map((item) => ({
					input_kind: item.input_kind,
					csv_instructions: finalPrompt,
					tab_instructions: {},
					sheet_name: null,
					transform: true,
				})),
			});
			fileIdByTransformItemId.current = new Map(
				job.items.flatMap((item) => {
					const source = filesRef.current[item.ordinal];
					return source ? [[item.id, source.id]] : [];
				}),
			);
			transformOutputIds.current.clear();
			setTransformFailures({});
			setTransformed({});
			setTransformJobId(job.id);
			setPhase("transform");
			onJobCreated?.(job);
		} catch (error) {
			trackError("auto_prompt_transform_error", { department_id: departmentId, source_job_id: analysisJobId, error_message: errorMessage(error) });
		}
	};

	const updateOutcome = (fileId: string, partial: Partial<TransformOutcome>) => {
		setTransformed((previous) => {
			const existing = previous[fileId];
			if (!existing) return previous;
			return { ...previous, [fileId]: { ...existing, ...partial } };
		});
	};

	const handleCancelTransform = (fileId: string) => {
		setFiles((previous) => previous.filter((file) => file.id !== fileId));
		setTransformed((previous) => {
			const next = { ...previous };
			delete next[fileId];
			return next;
		});
		setTransformFailures((previous) => {
			const next = { ...previous };
			delete next[fileId];
			return next;
		});
	};

	const handleSkipFailedTransform = (fileId: string) => {
		setTransformFailures((previous) => {
			const existing = previous[fileId];
			if (!existing) return previous;
			return { ...previous, [fileId]: { ...existing, skip: true } };
		});
	};

	const handleRetryTransform = async (fileId: string) => {
		if (!transformJob) return;
		const failure = transformFailures[fileId];
		if (!failure) return;
		setTransformFailures((previous) => {
			const next = { ...previous };
			delete next[fileId];
			return next;
		});
		transformOutputIds.current.delete(failure.itemId);
		try {
			await retryTransformItem.mutateAsync({ departmentId: transformJob.department_id, jobId: transformJob.id, itemId: failure.itemId });
		} catch {
			setTransformFailures((previous) => ({ ...previous, [fileId]: failure }));
		}
	};

	const loadFnForKind = (kind: AutoPromptFileKind) => {
		if (kind === "roster") return loadNewNurses;
		if (kind === "shifts") return loadScheduleShifts;
		return loadRecurringShifts;
	};

	const uploadFile = async (file: AutoPromptFile): Promise<boolean> => {
		if (!departmentId) return false;
		const outcome = transformed[file.id];
		if (!outcome || outcome.skip || outcome.uploaded) return true;
		const filenamePrefix = file.kind === "roster" ? "transformed_nurses" : "transformed";
		const blob = outcome.filteredBlob ? outcome.filteredBlob : new Blob([outcome.csvBuffer], { type: "text/csv" });
		const csvFile = new File([blob], `${filenamePrefix}_${stripExtension(file.file.name, file.kind)}.csv`, { type: "text/csv" });
		try {
			const result = await loadFnForKind(file.kind)({
				departmentId,
				file: csvFile,
				...(outcome.promptHistoryId ? { promptHistoryId: outcome.promptHistoryId } : {}),
			});
			// Each loader's success type is one member of TransformOutcome's explicit result union.
			updateOutcome(file.id, { uploaded: true, loadResult: result as TransformOutcome["loadResult"] });
			track("auto_prompt_upload_success", { department_id: departmentId, kind: file.kind });
			return true;
		} catch (error) {
			trackError("auto_prompt_upload_error", { department_id: departmentId, kind: file.kind, error_message: errorMessage(error) });
			return false;
		}
	};

	const needsWeekParityConfirmation = (file: AutoPromptFile): boolean => {
		const outcome = transformed[file.id];
		return file.kind === "template" && Boolean(outcome) && !outcome?.skip && !outcome?.uploaded && Boolean(chosenDepartment?.first_sunday);
	};

	const uploadRemainingTemplates = async () => {
		for (const file of orderedFilesOfKind("template")) {
			if (!(await uploadFile(file))) return;
		}
	};

	const handleUploadAll = async () => {
		if (!departmentId || artifactsUnavailable) return;
		track("auto_prompt_upload_all_start", { department_id: departmentId, file_count: orderedFiles.length });
		for (const file of orderedFiles) {
			if (transformFailures[file.id]?.skip) continue;
			if (needsWeekParityConfirmation(file)) {
				setShowWeekParityDialog(true);
				return;
			}
			if (!(await uploadFile(file))) return;
		}
	};

	const handleSwitchAndUploadTemplate = async () => {
		if (!chosenDepartment?.department_id || !chosenDepartment.first_sunday) return;
		const newFirstSunday = chosenDepartment.first_sunday.plus({ weeks: 1 });
		try {
			await performUpdateFirstSunday({ departmentId: chosenDepartment.department_id, firstSunday: newFirstSunday.toFormat("yyyy-MM-dd") });
			setChosenDepartment((previous) => ({ ...previous, first_sunday: newFirstSunday }));
			setShowWeekParityDialog(false);
			await uploadRemainingTemplates();
		} catch {
			// The mutation surfaces the error and records it with department context.
		}
	};

	const handleKeepAsIsAndUploadTemplate = async () => {
		setShowWeekParityDialog(false);
		await uploadRemainingTemplates();
	};

	const isAnalyzing = createAnalysisJob.isPending || isOnboardingJobActive(analysisJob);
	const analysisFailed = analysisJob?.status === "failed" || analysisJob?.status === "cancelled";
	const isTransforming = createTransformJob.isPending || isOnboardingJobActive(transformJob);
	const isUploadAllBusy = isLoadingNurses || isLoadingShifts || isLoadingTemplate;
	const allRemainingUploaded =
		files.length > 0 &&
		files.every((file) => {
			const outcome = transformed[file.id];
			if (outcome) return outcome.skip || outcome.uploaded;
			return transformFailures[file.id]?.skip === true;
		});
	const failedTransformFiles = files.filter((file) => Boolean(transformFailures[file.id]));

	return {
		phase,
		files,
		orderedFiles,
		loadOrderEntries,
		hasAtLeastOneFile,
		addFiles,
		handleKindChange,
		handleDeleteFile,
		effort,
		setEffort,
		editable,
		setEditable,
		flags,
		isAnalyzing,
		analysisFailed,
		hasRanAnalysis,
		handleAnalyze,
		finalPrompt,
		setFinalPrompt,
		handleGeneratePrompt,
		handleCopyPrompt,
		handleBackToEditing,
		transformed,
		transformFailures,
		isTransforming,
		handleTransformAll,
		updateOutcome,
		handleCancelTransform,
		handleSkipFailedTransform,
		handleRetryTransform,
		retryingTransformItemId: retryTransformItem.isPending ? retryTransformItem.variables?.itemId : undefined,
		isUploadAllBusy,
		allRemainingUploaded,
		failedTransformFiles,
		handleUploadAll,
		chosenDepartment,
		showWeekParityDialog,
		setShowWeekParityDialog,
		handleSwitchAndUploadTemplate,
		handleKeepAsIsAndUploadTemplate,
		isUpdatingFirstSunday,
		departmentId,
		resetWizard,
		artifactsUnavailable,
	};
}
